import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { orderBy } from 'lodash';
import { toast } from 'react-toastify';

import * as Core from '../../../core';
import ClassSessionGamePanel from '../classSessionGamePanel';
import { Avatar, SolidButton, StatusIndicator } from '../../../components/buttons-visuals';
import { ContentContainer } from '../../../components/containers';
import withLoading, { WithLoadingProps } from '../../../components/withLoading';
import { useLeague, useUserPermissionService } from '../../../hooks/store';
import { MatchService } from '../../../services/matchService';
import PlatformEventsService from '../../../services/platformEventsService';

import './index.scss';

const MATCH_PARTICIPANT_JOINED = 'MatchParticipantJoined';
const MATCH_STATE_CHANGED = 'MatchStateChanged';

interface ClassSessionLobbyProps extends WithLoadingProps {
    canConfigureClassSession: boolean;
    canEditSeason: boolean;
    canJoinClassSession: boolean;
    isJoiningClassSession: boolean;
    isMyMatch: boolean;
    match: Core.Models.MatchDetails;
    onJoinClassSession: () => void;
    reloadMatchData: () => Promise<void>;
}

const getParticipantDisplayName = (participant: Core.Models.MatchParticipant): string => {
    const user = orderBy(participant.users, (u) => u.sortOrder)[0];
    if (user) {
        return Core.Identity.renderMemberName(user) || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    }
    return participant.name || 'Student';
};

const ClassSessionLobby = (props: ClassSessionLobbyProps): JSX.Element => {
    const {
        canConfigureClassSession,
        canEditSeason,
        canJoinClassSession,
        isJoiningClassSession,
        isMyMatch,
        match,
        onJoinClassSession,
        reloadMatchData,
        setError,
        setIsLoading,
    } = props;

    const league = useLeague();
    const userPermissionService = useUserPermissionService();
    const userId = userPermissionService.getUserId();

    const [matchParticipants, setMatchParticipants] = useState<Core.Models.MatchParticipant[] | undefined>(undefined);
    const [enteredIds, setEnteredIds] = useState<Set<string>>(new Set());

    const loadParticipants = useCallback(async () => {
        try {
            setMatchParticipants(await MatchService.getMatchParticipants(match.id));
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    }, [match.id, setError, setIsLoading]);

    useEffect(() => {
        void loadParticipants();
    }, [loadParticipants]);

    useEffect(() => {
        if (!league?.id) return;
        let listenerId: string | null = null;
        (async () => {
            listenerId = await PlatformEventsService.startListening(
                [MATCH_PARTICIPANT_JOINED, MATCH_STATE_CHANGED],
                league.id
            );
        })();
        return () => {
            (async () => {
                if (listenerId) {
                    await PlatformEventsService.stopListening(listenerId, [
                        MATCH_PARTICIPANT_JOINED,
                        MATCH_STATE_CHANGED,
                    ]);
                }
            })();
        };
    }, [league?.id]);

    const handleParticipantJoined = useCallback(
        async (payload: { matchId?: string; MatchId?: string; displayName?: string; DisplayName?: string }) => {
            const matchId = payload.matchId || payload.MatchId;
            if (matchId !== match.id) return;
            const name = payload.displayName || payload.DisplayName;
            if (name) toast.info(`${name} joined the session.`);
            const before = new Set((matchParticipants ?? []).map((p) => p.id));
            try {
                const next = await MatchService.getMatchParticipants(match.id);
                const newlyJoined = next.filter((p) => !before.has(p.id)).map((p) => p.id);
                if (newlyJoined.length) {
                    setEnteredIds((prev) => new Set([...prev, ...newlyJoined]));
                }
                setMatchParticipants(next);
            } catch (e) {
                setError(e);
            }
            await reloadMatchData();
        },
        [match.id, matchParticipants, reloadMatchData, setError]
    );

    const handleMatchStateChanged = useCallback(
        async (matchId: string) => {
            if (matchId !== match.id) return;
            await loadParticipants();
            await reloadMatchData();
        },
        [loadParticipants, match.id, reloadMatchData]
    );

    useEffect(() => {
        const subscription = PlatformEventsService.dataReceived.subscribe(
            ({ eventName, data }: { eventName: string; data: any }) => {
                if (eventName === MATCH_PARTICIPANT_JOINED) void handleParticipantJoined(data);
                if (eventName === MATCH_STATE_CHANGED) void handleMatchStateChanged(data);
            }
        );
        return () => subscription.unsubscribe();
    }, [handleMatchStateChanged, handleParticipantJoined]);

    const statusLabel = useMemo(() => {
        if (match.currentState >= Core.Models.MatchState.IsComplete) return 'Session ended';
        if (match.currentState >= Core.Models.MatchState.InProgress) return 'Session live';
        if (isMyMatch) return "You're in the lobby";
        return 'Not joined yet';
    }, [isMyMatch, match.currentState]);

    const waitingMessage = useMemo(() => {
        if (match.currentState >= Core.Models.MatchState.IsComplete) {
            return 'This class session has ended.';
        }
        if (match.currentState >= Core.Models.MatchState.InProgress) {
            if (canConfigureClassSession) {
                return 'Session is live. Paste the QuizWhizzer access code below so students can join the game with their locked names.';
            }
            if (isMyMatch) {
                return 'Your professor has started the session. Open QuizWhizzer with your locked join link below when it appears.';
            }
            return 'This session has already started and you are not on the roster.';
        }
        if (canConfigureClassSession) {
            return 'Students are joining the lobby. When everyone is ready, start the session, open QuizWhizzer, then paste the access code here so students can join the game.';
        }
        if (isMyMatch) {
            return 'Waiting for your professor to start this QuizWhizzer session. Stay on this page — your join link will appear here once they begin.';
        }
        return 'You have not joined yet. Use Join session above to get on the roster.';
    }, [canConfigureClassSession, isMyMatch, match.currentState]);

    const orderedParticipants = useMemo(
        () => orderBy(matchParticipants ?? [], (p) => p.sortOrder),
        [matchParticipants]
    );

    const canImportScores =
        (canEditSeason || canConfigureClassSession) &&
        match.currentState > Core.Models.MatchState.RescheduleRequested &&
        match.stageState < Core.Models.StageState.IsComplete;

    if (!matchParticipants) return <></>;

    return (
        <div className="class-session-lobby">
            <ContentContainer
                className="class-session-lobby__panel shadow-medium"
                shade={Core.Models.Shades.Dark20}
                radius={Core.Models.RadiusSizes.Large}
            >
                <div className="class-session-lobby__header">
                    <h3 className="class-session-lobby__heading">Session lobby</h3>
                    <span className="class-session-lobby__count">
                        {orderedParticipants.length}{' '}
                        {orderedParticipants.length === 1 ? 'student' : 'students'}
                    </span>
                </div>

                <div className="class-session-lobby__body">
                    <div className="class-session-lobby__status">
                        <StatusIndicator
                            color={
                                match.currentState >= Core.Models.MatchState.IsComplete
                                    ? 'default'
                                    : match.currentState >= Core.Models.MatchState.InProgress
                                      ? 'success'
                                      : canJoinClassSession
                                        ? 'warning'
                                        : isMyMatch
                                          ? 'success'
                                          : 'warning'
                            }
                            outlineOnly
                        >
                            {statusLabel}
                        </StatusIndicator>
                        <p className="class-session-lobby__status-copy">{waitingMessage}</p>
                        {canJoinClassSession && (
                            <div className="class-session-lobby__join">
                                <SolidButton
                                    as="button"
                                    attention
                                    shadow
                                    size="xlarge"
                                    onClick={onJoinClassSession}
                                    pending={isJoiningClassSession}
                                >
                                    Join session
                                </SolidButton>
                            </div>
                        )}
                    </div>

                    {orderedParticipants.length === 0 ? (
                        <p className="class-session-lobby__empty">
                            {canJoinClassSession
                                ? 'No one has joined yet — tap Join session to be first.'
                                : 'No one has joined yet. Be the first in the lobby.'}
                        </p>
                    ) : (
                        <ul className="class-session-lobby__grid">
                            {orderedParticipants.map((participant) => {
                                const user = orderBy(participant.users, (u) => u.sortOrder)[0];
                                const displayName = getParticipantDisplayName(participant);
                                const isMe = !!user?.userId && user.userId === userId;
                                return (
                                    <li
                                        key={participant.id}
                                        className={classNames('class-session-lobby__person', {
                                            'class-session-lobby__person--me': isMe,
                                            'class-session-lobby__person--enter': enteredIds.has(participant.id),
                                        })}
                                    >
                                        <Avatar
                                            fallback="user"
                                            size="medium"
                                            src={user?.avatarUrl || participant.avatarUrl}
                                        />
                                        <div className="class-session-lobby__person-meta">
                                            <span className="class-session-lobby__person-name">{displayName}</span>
                                            {isMe && <span className="class-session-lobby__you">You</span>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </ContentContainer>

            {match.currentState > Core.Models.MatchState.RescheduleRequested && (
                <ClassSessionGamePanel
                    canConfigureAsHost={canConfigureClassSession || canEditSeason}
                    canImportScores={canImportScores}
                    isMyMatch={isMyMatch}
                    match={match}
                    matchParticipants={matchParticipants}
                    reloadMatchData={reloadMatchData}
                    reloadMatchParticipants={loadParticipants}
                />
            )}
        </div>
    );
};

export default withLoading(ClassSessionLobby, {
    loadingProps: { blockItem: true },
});
